import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Button,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  useDisclosure,
  useToast,
  IconButton,
  Box,
  Flex,
  Select,
  InputGroup,
  InputLeftElement,
  Badge,
  Collapse,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { ExternalLinkIcon, DeleteIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@chakra-ui/icons';
import { Sample } from '../../types/sample';
import { useState, useMemo } from 'react';

interface SampleTableProps {
  samples: Sample[];
  onRefresh?: () => void;
}

const statusColor = (status: string) => {
  if (status === 'Successful') return 'green.500';
  if (status === 'Failed') return 'red.500';
  return 'muted.400'; // Running
};

const deriveStatus = (sample: Sample): string => {
  // Get raw status from sample or infer from progress
  let raw: string | number = (sample as any).status;

  // Fallback if status is missing but progress exists
  if (!raw && typeof sample.progress === 'number') {
    if (sample.progress >= 100) raw = 'completed';
    else if (sample.progress < 0) raw = 'failed';
    else raw = 'running';
  } else if (!raw) {
    raw = 'running'; // Default to running if no info
  }

  const s = String(raw).toLowerCase();

  // Map to strictly: Successful, Failed, Running
  if (s === 'completed' || s === 'successful' || s === 'success' || s === '100%') {
    return 'Successful';
  }
  if (s.includes('fail') || s === 'cancelled' || s === 'error' || s === 'aborted') {
    return 'Failed';
  }

  return 'Running';
};

const displayName = (sample: Sample) => {
  const pid = (sample as any).patientId ?? (sample as any).patient_id;
  const sid = sample.name || (sample as any).sampleId || (sample as any).sample_id;
  return pid ? `${pid} / ${sid}` : sid;
};

const getResultsPageUrl = (jobId: string, pipelineType: string): string => {
  const baseUrl = window.location.origin;
  
  // Map pipeline types to their results page routes
  if (pipelineType === 'Haplotype Caller' || pipelineType?.toLowerCase().includes('haplotype')) {
    return `${baseUrl}/germline-mutation/haplotype-caller?jobId=${jobId}&view=completed`;
  }
  
  if (pipelineType === 'HLA Typing' || pipelineType?.toLowerCase().includes('hla')) {
    return `${baseUrl}/hla-typing?jobId=${jobId}&view=completed`;
  }
  
  if (pipelineType === 'VCF2TXT' || pipelineType?.toLowerCase().includes('vcf2txt')) {
    return `${baseUrl}/vcf2txt?jobId=${jobId}&view=completed`;
  }
  
  // Default to PGx Extractor (ProPEx) for PGx Analysis or unknown types
  return `${baseUrl}/pgx-extractor?jobId=${jobId}&view=completed`;
};

const handleViewResults = (jobId: string, pipelineType: string) => {
  const url = getResultsPageUrl(jobId, pipelineType);
  window.open(url, '_blank');
};

// Helper to get delete API endpoint based on pipeline type
const getDeleteApiEndpoint = (jobId: string, pipelineType: string): string => {
  const baseUrl = import.meta.env.VITE_API_URL;
  
  if (pipelineType === 'Haplotype Caller' || pipelineType?.toLowerCase().includes('haplotype')) {
    return `${baseUrl}/api/v1/haplotype-caller/jobs/${jobId}`;
  }
  
  if (pipelineType === 'HLA Typing' || pipelineType?.toLowerCase().includes('hla')) {
    return `${baseUrl}/api/v1/hla-typing/jobs/${jobId}`;
  }
  
  if (pipelineType === 'VCF2TXT' || pipelineType?.toLowerCase().includes('vcf2txt')) {
    return `${baseUrl}/api/v1/vcf2txt/jobs/${jobId}`;
  }
  
  // Default to PGx Extractor (ProPEx) for PGx Analysis or unknown types
  return `${baseUrl}/api/v1/pgx/jobs/${jobId}`;
};

const SampleTable = ({ samples, onRefresh }: SampleTableProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [jobToDelete, setJobToDelete] = useState<{ id: string; pipelineType: string } | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnalysis, setFilterAnalysis] = useState('');
  const [filterDataType, setFilterDataType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Extract unique values for dropdowns
  const uniqueAnalysis = useMemo(() => {
    const values = new Set(samples.map(s => s.analysis).filter(Boolean));
    return Array.from(values).sort();
  }, [samples]);

  const uniqueDataTypes = useMemo(() => {
    const values = new Set(samples.map(s => (s as any).type ?? (s as any).dataType ?? '').filter(Boolean));
    return Array.from(values).sort();
  }, [samples]);

  // Filter samples
  const filteredSamples = useMemo(() => {
    return samples.filter(sample => {
      // Search query filter (sample name)
      if (searchQuery) {
        const name = displayName(sample).toLowerCase();
        if (!name.includes(searchQuery.toLowerCase())) return false;
      }

      // Analysis filter
      if (filterAnalysis && sample.analysis !== filterAnalysis) return false;

      // Data type filter
      const dataType = (sample as any).type ?? (sample as any).dataType ?? '';
      if (filterDataType && dataType !== filterDataType) return false;

      // Status filter
      if (filterStatus) {
        const status = deriveStatus(sample);
        if (status !== filterStatus) return false;
      }

      // Date range filter
      if (filterDateFrom || filterDateTo) {
        const sampleDate = sample.date;
        if (sampleDate && sampleDate !== 'N/A') {
          // Parse date (expected format: YYYY-MM-DD HH:MM or similar)
          const datePart = sampleDate.split(' ')[0];
          if (filterDateFrom && datePart < filterDateFrom) return false;
          if (filterDateTo && datePart > filterDateTo) return false;
        }
      }

      return true;
    });
  }, [samples, searchQuery, filterAnalysis, filterDataType, filterStatus, filterDateFrom, filterDateTo]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (filterAnalysis) count++;
    if (filterDataType) count++;
    if (filterStatus) count++;
    if (filterDateFrom) count++;
    if (filterDateTo) count++;
    return count;
  }, [searchQuery, filterAnalysis, filterDataType, filterStatus, filterDateFrom, filterDateTo]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterAnalysis('');
    setFilterDataType('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const initiateDelete = (jobId: string, pipelineType: string) => {
    setJobToDelete({ id: jobId, pipelineType });
    setConfirmText('');
    onOpen();
  };

  const handleConfirmDelete = async () => {
    if (confirmText !== 'delete' || !jobToDelete) return;

    setIsDeleting(true);
    const token = localStorage.getItem('access_token');
    try {
      const deleteUrl = getDeleteApiEndpoint(jobToDelete.id, jobToDelete.pipelineType);
      const res = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to delete job');
      }

      toast({
        title: 'Job deleted.',
        description: "The job and its data have been successfully deleted.",
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (onRefresh) {
        onRefresh();
      } else {
        window.location.reload();
      }

    } catch (e) {
      console.error('Delete failed', e);
      toast({
        title: 'Delete failed.',
        description: "Something went wrong while deleting the job.",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsDeleting(false); // Only need to reset if we don't reload
    }
  };

  return (
    <>
      {/* Filter Bar */}
      <Box mb={4}>
        {/* Filter Toggle & Summary */}
        <Flex justify="space-between" align="center" mb={2}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={showFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}
            onClick={() => setShowFilters(!showFilters)}
            color="primary.500"
          >
            Filters
            {activeFilterCount > 0 && (
              <Badge ml={2} colorScheme="purple" borderRadius="full">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          
          <HStack spacing={2}>
            {activeFilterCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<CloseIcon boxSize={2} />}
                onClick={clearAllFilters}
                color="muted.500"
                _hover={{ color: "error.500" }}
              >
                Clear all
              </Button>
            )}
            <Text fontSize="sm" color="muted.500">
              {filteredSamples.length} of {samples.length} results
            </Text>
          </HStack>
        </Flex>

        {/* Collapsible Filter Panel */}
        <Collapse in={showFilters} animateOpacity>
          <Box
            p={4}
            bg="surface.50"
            borderRadius="12px"
            borderWidth="1px"
            borderColor="surface.200"
          >
            <VStack spacing={4} align="stretch">
              {/* Row 1: Search + Status */}
              <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                <Box flex={2}>
                  <Text fontSize="xs" fontWeight="medium" color="muted.500" mb={1}>
                    Search
                  </Text>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="muted.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by sample name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      bg="white"
                      borderRadius="8px"
                      focusBorderColor="primary.500"
                    />
                  </InputGroup>
                </Box>
                
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color="muted.500" mb={1}>
                    Status
                  </Text>
                  <Select
                    size="sm"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    placeholder="All statuses"
                    bg="white"
                    borderRadius="8px"
                    focusBorderColor="primary.500"
                  >
                    <option value="Successful">Successful</option>
                    <option value="Running">Running</option>
                    <option value="Failed">Failed</option>
                  </Select>
                </Box>
              </Flex>

              {/* Row 2: Analysis + Data Type */}
              <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color="muted.500" mb={1}>
                    Analysis
                  </Text>
                  <Select
                    size="sm"
                    value={filterAnalysis}
                    onChange={(e) => setFilterAnalysis(e.target.value)}
                    placeholder="All analyses"
                    bg="white"
                    borderRadius="8px"
                    focusBorderColor="primary.500"
                  >
                    {uniqueAnalysis.map(analysis => (
                      <option key={analysis} value={analysis}>{analysis}</option>
                    ))}
                  </Select>
                </Box>
                
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color="muted.500" mb={1}>
                    Data Type
                  </Text>
                  <Select
                    size="sm"
                    value={filterDataType}
                    onChange={(e) => setFilterDataType(e.target.value)}
                    placeholder="All types"
                    bg="white"
                    borderRadius="8px"
                    focusBorderColor="primary.500"
                  >
                    {uniqueDataTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Select>
                </Box>
              </Flex>

              {/* Row 3: Date Range */}
              <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color="muted.500" mb={1}>
                    Date From
                  </Text>
                  <Input
                    size="sm"
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    bg="white"
                    borderRadius="8px"
                    focusBorderColor="primary.500"
                  />
                </Box>
                
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color="muted.500" mb={1}>
                    Date To
                  </Text>
                  <Input
                    size="sm"
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    bg="white"
                    borderRadius="8px"
                    focusBorderColor="primary.500"
                  />
                </Box>
              </Flex>
            </VStack>
          </Box>
        </Collapse>
      </Box>

      <TableContainer
        borderWidth="1px"
        borderColor="surface.300"
        borderRadius="12px"
        overflow="hidden"
        boxShadow="0 1px 3px rgba(26, 18, 98, 0.08)"
        bg="white"
      >
        <Table variant="rounded">
          <Thead>
            <Tr>
              <Th>Sample Name</Th>
              <Th>Analysis</Th>
              <Th>Date</Th>
              <Th>Data Type</Th>
              <Th textAlign="center">Status</Th>
              <Th textAlign="center">Results</Th>
              <Th textAlign="center">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredSamples.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={8}>
                  <Text color="muted.400">
                    {samples.length === 0 ? 'No samples found' : 'No samples match the current filters'}
                  </Text>
                  {activeFilterCount > 0 && (
                    <Button
                      size="sm"
                      variant="link"
                      color="primary.500"
                      mt={2}
                      onClick={clearAllFilters}
                    >
                      Clear filters
                    </Button>
                  )}
                </Td>
              </Tr>
            ) : (
              filteredSamples.map((sample) => {
                const status = deriveStatus(sample);
                const jobId = (sample as any).job_id || sample.id;
                return (
                  <Tr key={jobId}>
                    <Td>{displayName(sample)}</Td>
                    <Td>{sample.analysis}</Td>
                    <Td>{sample.date}</Td>
                    <Td>{(sample as any).type ?? (sample as any).dataType ?? 'BAM File'}</Td>
                    <Td textAlign="center">
                      <Text as="span" fontWeight="semibold" color={statusColor(status)}>
                        {status}
                      </Text>
                    </Td>
                    <Td textAlign="center">
                      <Button
                        size="sm"
                        leftIcon={<ExternalLinkIcon />}
                        bg="primary.500"
                        color="white"
                        variant="solid"
                        _hover={{ bg: "primary.600" }}
                        _disabled={{ bg: "muted.400", opacity: 1, cursor: "not-allowed" }}
                        isDisabled={status !== 'Successful'}
                        onClick={() => handleViewResults(String(jobId), sample.analysis)}
                      >
                        View
                      </Button>
                    </Td>
                    <Td textAlign="center">
                      <IconButton
                        aria-label="Delete job"
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => initiateDelete(String(jobId), sample.analysis)}
                      />
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="rgba(26, 18, 98, 0.4)" />
        <ModalContent borderRadius="16px" mx={4}>
          <ModalHeader color="primary.500" fontWeight="bold">
            Confirm Deletion
          </ModalHeader>
          <ModalCloseButton color="muted.400" />
          <ModalBody>
            <Text mb={4} color="primary.500">
              Are you sure you want to delete this job? This will remove all associated results and input files.
              <br />
              <br />
              Type <Text as="strong" color="error.500">delete</Text> below to confirm.
            </Text>
            <Input
              placeholder="Type 'delete'"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              focusBorderColor="error.500"
            />
          </ModalBody>

          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isDeleting}>
              Cancel
            </Button>
            <Button
              bg="error.500"
              color="white"
              _hover={{ bg: "error.600" }}
              onClick={handleConfirmDelete}
              isDisabled={confirmText !== 'delete'}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SampleTable;
